import { useCalculateFetchedItems } from '../../hooks/useCalculateFetchItems';
import { useSyncHorizontalScroll } from '../../hooks/useSyncHorizontalScroll';
import React, { useCallback, useRef, useEffect, useState } from 'react';
import { useScrollFetchData } from '../../hooks/useScrollFetchData';
import CustomHeaderCell from '../../utils/customHeaderCell';
import UsersListViewData from './usersListViewData';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PersistedFixedSizeList as FixedSizeList } from "../../hooks/listNavigation";
import { Box } from '@mui/material';
import { debounce } from 'lodash';

const defaultColumnWidth = {
    name: 350,
    email: 500,
    role: 250,
    region: 250,
};

const UsersListView = React.memo(({ scrollRef, filteredUsersCount, setUsersFetched, users, setOffset, regions, sortBy, setSortBy }) => {

    const { t } = useTranslation();
    const navigate = useNavigate();

    const [userColumnWidth, setUserColumnWidth] = useState(() => {
        const storedWidths = JSON.parse(localStorage.getItem('userColumnWidth')) || {};
        return { ...defaultColumnWidth, ...storedWidths }; // Merge defaults with stored widths
    });

    const [windowHeight, setWindowHeight] = useState(window.innerHeight);

    // Update window height on resize
    useEffect(() => {
        const handleResize = debounce(() => {
            setWindowHeight(window.innerHeight);
        }, 300); // 300ms debounce delay

        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
            handleResize.cancel(); // Cancel any pending debounced calls
        };
    }, []);

    const tableHeadRef = useRef(null);

    // synchronize horizontal scroll between table head and list
    useSyncHorizontalScroll(tableHeadRef, scrollRef);

    // Calculate fetched items on mount
    useCalculateFetchedItems(filteredUsersCount, setUsersFetched, 70, 280);

    // Handle scroll and fetch more items
    const handleScrollList = useScrollFetchData(setOffset, setUsersFetched, filteredUsersCount, 70, 280);


    const handleUserClick = useCallback((id) => {
        navigate(`utilisateur_details/${id}`);
    }, [navigate]);

    const handleSort = (field) => {
        setSortBy((prevSortBy) => {
            const nextSortOrder = prevSortBy[field] === null ? 'asc'
                : prevSortBy[field] === 'asc' ? 'desc'
                    : null;

            return Object.fromEntries(
                Object.keys(prevSortBy)?.map((key) => [key, key === field ? nextSortOrder : null])
            );
        });

    };

    const handleColumnWidthChange = (value, columnKey) => {
        setUserColumnWidth({ ...userColumnWidth, [columnKey]: value })
    }

    useEffect(() => {
        localStorage.setItem('userColumnWidth', JSON.stringify(userColumnWidth));
    }, [userColumnWidth]);

    const Row = ({ index, style, data }) => {
        const { users, handleUserClick } = data;
        const user = index === 0 ? users[0] : users[index];
        return (
            <Box key={index} style={{ ...style, width: 'fit-content' }}>
                <UsersListViewData
                    user={user}
                    columnWidth={userColumnWidth}
                    handleUserClick={handleUserClick}
                />
            </Box>
        );
    };

    return (
        <Box className='table'>
            <Box className='tableHead' ref={tableHeadRef}>
                <CustomHeaderCell
                    className='table_header_cell_non_filter'
                    label='Nom et prénom'
                    columnKey='name'
                    sortBy={sortBy}
                    handleSort={handleSort}
                    sortable={true}
                    isFilter={false}
                    isFixed={true}
                    columnWidth={userColumnWidth}
                    onColumnWidthChange={handleColumnWidthChange}
                />
                <CustomHeaderCell
                    className='table_header_cell_non_filter'
                    label={t('Adresse e-mail')}
                    columnKey='email'
                    sortBy={sortBy}
                    handleSort={handleSort}
                    sortable={true}
                    isFilter={false}
                    columnWidth={userColumnWidth}
                    onColumnWidthChange={handleColumnWidthChange}
                />
                <CustomHeaderCell
                    className='table_header_cell_non_filter'
                    label={t('Rôle')}
                    columnKey='role'
                    sortBy={sortBy}
                    handleSort={handleSort}
                    sortable={true}
                    isFilter={false}
                    columnWidth={userColumnWidth}
                    onColumnWidthChange={handleColumnWidthChange}
                />
                <CustomHeaderCell
                    className='table_header_cell_non_filter'
                    label={t('Regions')}
                    columnKey='region'
                    sortable={false}
                    isFilter={false}
                    columnWidth={userColumnWidth}
                    onColumnWidthChange={handleColumnWidthChange}
                />
                <CustomHeaderCell
                    className='table_header_cell_non_filter' width="50px" />
            </Box>
            <Box className='tableBody'>
                <FixedSizeList
                    ref={scrollRef}
                    height={Math.min(
                        filteredUsersCount * 70 + 15,
                        windowHeight - 285
                    )}
                    width='100%'
                    itemCount={filteredUsersCount}
                    itemSize={70}
                    itemData={{ users, regions, handleUserClick }}
                    onScroll={handleScrollList}
                >
                    {Row}
                </FixedSizeList>
            </Box>
        </Box>

    );
});

export default UsersListView;